import { useEffect, useState } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown, Instagram } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

export default function InstagramTab() {
  const qc = useQueryClient();
  const [items, setItems] = useState(null);
  const [url, setUrl] = useState("");
  const [views, setViews] = useState("");
  const [caption, setCaption] = useState("");

  const load = () => api.get("/instagram-videos").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
  }, []);

  if (!items) return <p className="font-mono text-xs uppercase tracking-[0.25em] text-subtle">Loading…</p>;

  const add = async () => {
    if (!url.includes("instagram.com")) return toast.error("Paste a valid Instagram post/reel link.");
    await api.post("/admin/instagram-videos", { url, views, caption });
    setUrl("");
    setViews("");
    setCaption("");
    toast.success("Instagram video added.");
    load();
    qc.invalidateQueries({ queryKey: ["site"] });
  };

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    setItems(next);
    await api.post("/admin/reorder", { collection: "instagram_videos", ids: next.map((v) => v.id) });
    qc.invalidateQueries({ queryKey: ["site"] });
  };

  const del = async (v) => {
    if (!window.confirm("Remove this video?")) return;
    await api.delete(`/admin/instagram-videos/${v.id}`);
    load();
    qc.invalidateQueries({ queryKey: ["site"] });
  };

  return (
    <div className="max-w-3xl">
      <div className="mb-8 rounded-3xl border border-line bg-white p-5 shadow-[0_14px_35px_-24px_rgba(13,13,17,0.18)]">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-mute">Add an Instagram video</p>
        <div className="flex flex-col gap-3 sm:flex-row">
          <input
            data-testid="admin-instagram-url-input"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.instagram.com/reel/…"
            className="flex-1 border border-line bg-white px-3.5 py-2.5 text-sm outline-none focus:border-srs"
          />
          <input
            value={views}
            onChange={(e) => setViews(e.target.value)}
            placeholder="Views (e.g. 12.4K)"
            className="border border-line bg-white px-3.5 py-2.5 text-sm outline-none focus:border-srs sm:w-40"
          />
          <input
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Caption (optional)"
            className="border border-line bg-white px-3.5 py-2.5 text-sm outline-none focus:border-srs sm:w-52"
          />
          <button data-testid="admin-instagram-add-button" onClick={add} className="flex items-center justify-center gap-2 bg-ink px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] text-white hover:bg-srs">
            <Plus size={14} /> Add
          </button>
        </div>
        <p className="mt-3 text-xs text-subtle">Views are entered manually — Instagram doesn't expose this publicly. Update the number here whenever you check the post yourself.</p>
      </div>

      <div className="divide-y divide-line border border-line bg-white" data-testid="admin-instagram-list">
        {items.map((v, i) => (
          <div key={v.id} className="flex items-center gap-4 p-4" data-testid={`admin-instagram-row-${i}`}>
            <span className="w-7 font-mono text-[11px] text-subtle">{String(i + 1).padStart(2, "0")}</span>
            <div className="flex h-12 w-12 items-center justify-center border border-line bg-mist text-mute">
              <Instagram size={18} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-ink">{v.caption || `Instagram video ${i + 1}`}</p>
              <p className="truncate font-mono text-[10px] text-subtle">{v.url} {v.views && `· ${v.views} views`}</p>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="p-2 text-mute hover:text-ink disabled:opacity-25" aria-label="Move up"><ArrowUp size={15} /></button>
              <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="p-2 text-mute hover:text-ink disabled:opacity-25" aria-label="Move down"><ArrowDown size={15} /></button>
              <button onClick={() => del(v)} className="p-2 text-mute hover:text-srs" aria-label="Delete"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
        {!items.length && <p className="p-8 text-center text-sm text-subtle">No Instagram videos yet.</p>}
      </div>
    </div>
  );
}
